import React, { useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'

import { prominenceLabelText } from '../../utils/search/search-results-util/search-results.util'

import {
    StyledListingStandout,
    StyledSearchListing,
    StyledSearchListingAdvert,
    StyledSearchListingAdvertColumn,
} from './search-listing.styles'

import { buildFpaUrl } from './fpa-link.util'
import { useScrollAdvertIntoView } from '../../hooks/use-scroll-advert-into-view'
import { observeResultsScroll } from '../../pages/search-results/results-scrolling-observer'
import styled from 'styled-components'
import { CarouselImages } from '../../components/carousel-images/carousel-images'
import { LeasingListingDetails } from '../../components/search-listing-details/leasing-listing-details'
import { StyledCarouselBadge } from '../../components/carousel-images/carousel-images.styles'
import { calculateGridPosition } from '../../utils/tracking/grid-position'
import { useTrackViewedInteraction } from '../../hooks/tracking/useTrackViewedInteraction.hook'
import { SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing } from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { useIsLeasingSearch } from '../../utils/is-leasing-search'
import { LeasingListingDetailsWithRepExampleModal } from '../../components/search-listing-details/leasing-listing-details-with-rep-example-modal'
import { useChannel, useFlexibleFinanceFilters, useNewLeasingCard } from '../../hooks/use-abtest.hook'
import { BadgeGroup, BadgeCategories } from '@at/design-system-elements'

const NoHeightDiv = styled.div`
    position: absolute;
`
interface LeasingListingProps {
    listing: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing
    index: number
}

export const LeasingListing = ({ listing }: LeasingListingProps) => {
    const {
        advertId,
        fpaLink,
        images,
        subTitle,
        title,
        type,
        trackingContext,
        numberOfImages,
        badges,
        finance,
        vatStatus,
    } = listing

    const {
        location: { search },
    } = useHistory()

    const channel = useChannel()

    const isLeasingSearch = useIsLeasingSearch()
    const prominenceLabel = isLeasingSearch ? null : prominenceLabelText(type)
    const url = buildFpaUrl(fpaLink, search, channel, isLeasingSearch, trackingContext.card.pageNumber)
    const scrollToRef = useScrollAdvertIntoView()
    const trackInteraction = useTrackViewedInteraction('search_card')
    const leasingListingGridRef = useRef<HTMLDivElement | null>(null)
    const trackingElementRef = useCallback(
        (trackingElementRef: HTMLDivElement) => {
            observeResultsScroll(trackingElementRef, type, trackInteraction, {
                ...trackingContext,
                gridPosition: calculateGridPosition(trackingElementRef, trackingContext.card.position),
            })
        },
        [type, trackInteraction, trackingContext]
    )
    const showNewLeasingCard = useNewLeasingCard()
    const showLeasingDetailsWithRepExampleModal = isLeasingSearch || showNewLeasingCard
    const isFlexibleFinance = useFlexibleFinanceFilters()

    return (
        <StyledSearchListing id={advertId} ref={scrollToRef}>
            {prominenceLabel ? (
                <StyledListingStandout as="span" data-testid={type}>
                    {prominenceLabel}
                </StyledListingStandout>
            ) : null}

            <StyledSearchListingAdvert as="div" data-testid="advertCard">
                <CarouselImages
                    images={images}
                    title={title}
                    advertId={advertId}
                    saveAdvertIsSaved={false}
                    isLeasing={true}
                    badge={
                        isLeasingSearch ? null : (
                            <StyledCarouselBadge className={'new-leasing-button'}>
                                <BadgeGroup
                                    content={[
                                        {
                                            category: BadgeCategories.onContent,
                                            text: 'Lease deal',
                                        },
                                    ]}
                                />
                            </StyledCarouselBadge>
                        )
                    }
                    numberOfImages={numberOfImages}
                    fpaUrl={url}
                    type={type}
                    listingRef={leasingListingGridRef.current || undefined}
                    trackingContext={trackingContext}
                />

                <StyledSearchListingAdvertColumn ref={leasingListingGridRef} $isFlexibleFinance={isFlexibleFinance}>
                    {showLeasingDetailsWithRepExampleModal ? (
                        <LeasingListingDetailsWithRepExampleModal
                            advertId={advertId || ''}
                            fpaUrl={url}
                            subtitle={subTitle}
                            title={title}
                            type={type}
                            badges={badges}
                            finance={finance}
                            vatStatus={vatStatus}
                            trackingContext={trackingContext}
                            priceFormattedAndRounded={finance?.monthlyPrice.priceFormattedAndRounded}
                            leasingListingRef={leasingListingGridRef.current || undefined}
                        />
                    ) : (
                        <LeasingListingDetails
                            advertId={advertId || ''}
                            fpaUrl={url}
                            subtitle={subTitle}
                            title={title}
                            type={type}
                            badges={badges}
                            finance={finance}
                            trackingContext={trackingContext}
                            priceFormattedAndRounded={finance?.monthlyPrice.priceFormattedAndRounded}
                            leasingListingRef={leasingListingGridRef.current || undefined}
                        />
                    )}
                </StyledSearchListingAdvertColumn>
            </StyledSearchListingAdvert>
            <NoHeightDiv ref={trackingElementRef} />
        </StyledSearchListing>
    )
}
